import dayjs from 'dayjs';

import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';

const statusOrderMap = {
  [MatchStatus.LIVE]: 0,
  [MatchStatus.UPCOMING]: 1,
  [MatchStatus.POSTPONED]: 1,
  [MatchStatus.ABANDONED]: 1,
  [MatchStatus.CANCELED]: 1,
  [MatchStatus.UNKNOWN]: 1,
  [MatchStatus.FINISHED]: 2,
} as const;

export const statusSorting = (a: Match, b: Match) => statusOrderMap[a.status] - statusOrderMap[b.status];

export const parameterSorting = (a: string | number, b: string | number) => {
  if (!a || !b || a === b) return 0;
  if (typeof a === 'number' && typeof b === 'number') return b - a;
  if (typeof a === 'string' && typeof b === 'string') return a > b ? 1 : -1;

  return 0;
};

export const compareDates = (a: string, b: string) => {
  if (dayjs(a).isSame(b)) return 0;
  return dayjs(a).isAfter(b) ? 1 : -1;
};

export const dateTimeSorting = (a: Match, b: Match) => {
  // non defined matches at the end
  if (!a.kickOffTime?.dateTime) return 1;
  if (!b.kickOffTime?.dateTime) return -1;

  // order by datetime DESC
  if (a.status === MatchStatus.FINISHED && b.status === MatchStatus.FINISHED) {
    return compareDates(b.kickOffTime.dateTime, a.kickOffTime.dateTime);
  }

  // order by datetime ASC
  return compareDates(a.kickOffTime.dateTime, b.kickOffTime.dateTime);
};

const commonSorting = (a: Match, b: Match) => {
  let sort = statusSorting(a, b);

  if (sort === 0) {
    sort = dateTimeSorting(a, b);
  }

  return sort;
};

export const matchSorting = (customSort?: string) => (a: Match, b: Match) => {
  let sort = commonSorting(a, b);

  if (sort === 0) {
    switch (customSort) {
      case 'byLeague':
        sort = parameterSorting(
          a?.group?.league?.metaData?.leagueShortName,
          b?.group?.league?.metaData?.leagueShortName,
        );
        break;
      case 'byCompetition':
        sort = parameterSorting(a?.competition?.id, b?.competition?.id);
        break;
      default:
        break;
    }
  }

  if (sort === 0) {
    sort = Number.parseInt(b.id) - Number.parseInt(a.id);
  }

  return sort;
};
